import React, { createContext, useContext, useRef } from "react";
import { useAuth } from "../firebase/AuthProvider"; 
import { getParticipantInfo } from "../services/participantServices/ParticipantService"; // <-- Adjust to your actual path
import { TooltipParticipant } from "../components/ui/Tooltip/ParticipantProfileTooltip"; 

type ParticipantCacheEntry = {
  data: TooltipParticipant | null;
  expiresAt: number;
};

interface ParticipantProfileCacheContextType {
  getParticipantProfile: (participantId: string | number) => Promise<TooltipParticipant | null>;
}

const ParticipantProfileCacheContext = createContext<ParticipantProfileCacheContextType | null>(null);

export const ParticipantProfileCacheProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser } = useAuth();
  
  const profileCache = useRef<Map<string, ParticipantCacheEntry>>(new Map());
  const pendingFetches = useRef<Map<string, Promise<TooltipParticipant | null>>>(new Map());

  const fetchProfile = async (participantId: string | number): Promise<TooltipParticipant | null> => {
    const stringId = String(participantId);
    try {
      const token = await currentUser?.getIdToken();
      if (!token || !stringId) return null;

      // Call your specific API function
      const res = await getParticipantInfo(stringId, token);

      // Handle successful response (ignore "unauthorized" string or null)
      if (res && res !== "unauthorized") {
        
        // Explicitly map the AssignedParticipant to the TooltipParticipant
        // This completely solves the TypeScript TS2739 error!
        const participantData: TooltipParticipant = {
            id: res.id,
            firstName: res.firstName,
            lastName: res.lastName,
            email: res.email, // Map from participantEmail to email
            waiver: res.waiver,
            creationDate: res.creationDate,
            linkageDate: res.linkageDate,
            cpocDate: res.cpocDate,
            cpocEndDate: res.cpocEndDate,
            region: res.region // Pass the Region ID number straight through
        };

        profileCache.current.set(stringId, {
          data: participantData,
          expiresAt: Date.now() + 5 * 60 * 1000, // Cache for 5 mins
        });
        
        return participantData;
      }
      
      throw new Error("No data returned or unauthorized");
    } catch (error) {
      console.warn(`[Participant Cache] Failed to fetch for ${stringId}:`, error);
      
      profileCache.current.set(stringId, {
        data: null,
        expiresAt: Date.now() + 60 * 1000, // Negative cache 1 min
      });
      return null;
    }
  };

  const getParticipantProfile = async (participantId: string | number): Promise<TooltipParticipant | null> => {
    const stringId = String(participantId);
    const entry = profileCache.current.get(stringId);

    if (entry && entry.expiresAt > Date.now()) return entry.data;
    if (pendingFetches.current.has(stringId)) return pendingFetches.current.get(stringId)!;

    const fetchPromise = fetchProfile(stringId);
    pendingFetches.current.set(stringId, fetchPromise);

    const result = await fetchPromise;
    pendingFetches.current.delete(stringId); 

    return result;
  };

  return (
    <ParticipantProfileCacheContext.Provider value={{ getParticipantProfile }}>
      {children}
    </ParticipantProfileCacheContext.Provider>
  );
};

export const useParticipantProfileCache = (): ParticipantProfileCacheContextType => {
  const ctx = useContext(ParticipantProfileCacheContext);
  if (!ctx) {
    throw new Error("useParticipantProfileCache must be used inside ParticipantProfileCacheProvider");
  }
  return ctx;
};